/** The token of the confirmation link: 43 base64url characters in the fragment, never in the query. */
const TOKEN = /^[A-Za-z0-9_-]{43}$/;

/** Where a token waits for the login it needs: this tab's session storage, never a URL, a cookie or a log. */
const HELD_KEY = "smycles.application-confirmation";

/** Long enough for one login round trip, short enough not to outlive the visit that needed it. */
export const HELD_TOKEN_TTL_MS = 30 * 60_000;

/** Reads `#token=…` from a fragment; anything else is no token. */
export function tokenFromFragment(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get("token");
  return value && TOKEN.test(value) ? value : null;
}

/**
 * Keeps the token of a confirmation that needs an account across the login, in this tab only and for
 * {@link HELD_TOKEN_TTL_MS}. Without storage the login loses it; the link from the mail brings it back.
 */
export function holdToken(token: string, now = Date.now()): void {
  try {
    window.sessionStorage.setItem(HELD_KEY, JSON.stringify({ token, until: now + HELD_TOKEN_TTL_MS }));
  } catch {
    // Storage switched off or full: nothing is held.
  }
}

/** The held token while it is valid; an expired or malformed entry is removed on the way. */
export function heldToken(now = Date.now()): string | null {
  try {
    const raw = window.sessionStorage.getItem(HELD_KEY);
    if (raw === null) return null;
    const held = JSON.parse(raw) as { token?: unknown; until?: unknown };
    if (typeof held.token === "string" && TOKEN.test(held.token) && typeof held.until === "number" && held.until > now) {
      return held.token;
    }
  } catch {
    // Unreadable: treated as gone.
  }
  releaseToken();
  return null;
}

/** Forgets the held token: the confirmation reached an end, or a new link replaces it. */
export function releaseToken(): void {
  try {
    window.sessionStorage.removeItem(HELD_KEY);
  } catch {
    // Nothing to forget.
  }
}
